import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Session } from '../../entity/entities';
import { SecurityClient } from '../../../client/security.client';
import { isTabapayAiRoleAllowed } from '../../entity/enum/roleIds.enum';
import { ScheduleAssistantContextService } from './schedule-assistant-context.service';

/**
 * Which assistant a chat talks to. The topic is chosen per chat, not per message:
 * each one is a different agent with its own tools and its own data, and the server
 * refuses to continue a conversation under a different topic.
 */
export type ChatTopic = 'config' | 'recon' | 'schedule';

/**
 * A single rendered piece of a chat message: either plain text or a (non-PDF)
 * hyperlink. PDF references are pulled out of the prose entirely and shown as
 * separate cards (see PdfRef), so they never appear inline as text.
 */
interface ChatSegment {
  text: string;
  href?: string;
}

/**
 * A change the scheduling assistant proposed (US-8.113). Nothing has happened until the
 * user presses Confirm; the server then runs exactly what the card says, once.
 */
interface ScheduleActionCard {
  id: string;
  kind: string;
  title: string;
  details: string[];
  warnings: string[];
  /** pending | executing | executed | failed | cancelled | expired */
  status: string;
  resultMessage?: string;
  /** A confirm/cancel request is in flight. */
  busy?: boolean;
}

/** A PDF document referenced by a message, rendered as a click-to-open card. */
interface PdfRef {
  /** Original URI (s3://... for generated policies, or a plain http(s) link). */
  uri: string;
  /** File name shown on the card. */
  name: string;
}

interface ChatMessage {
  Role: 'user' | 'assistant';
  Content: string;
  Sent: string;
  /** Prose split into text + link segments (PDF links removed). */
  Parts?: ChatSegment[];
  /** PDF documents extracted from the message / response, shown as cards. */
  Pdfs?: PdfRef[];
  /** Suggested next questions returned with this turn (assistant only). */
  Followups?: string[];
  /** Scheduling assistant: changes proposed this turn, shown as confirm cards. */
  Actions?: ScheduleActionCard[];
}

/**
 * One chat tab: an independent conversation with its own running transcript,
 * threading id and in-flight spinner. The widget holds up to maxTabs of these.
 */
interface ChatSession {
  /** Running transcript; null until the first message is sent. */
  messages: ChatMessage[];
  /** Conversation id threaded across turns (null for a fresh chat). */
  chatId: string;
  /** Whether this tab currently has a request in flight. */
  showSpinner: boolean;
  /** Which assistant answers in this tab. Fixed once the conversation starts. */
  topic: ChatTopic;
  /** Scheduling topic: the location this chat is bound to (set on the first message). */
  customerCode?: number;
}

/**
 * AI ChatBot - Config Engine / TabaPay reconciliation in PAP, uClock scheduling
 * assistant in PDP (US-8.113). Extracted out of AppComponent so the whole
 * widget - floating button, conversation box, send logic and message rendering -
 * lives in one place. Two UX features layer on top of the plain transcript:
 *  - any PDF the agent references (an s3:// policy URI in the answer or in the
 *    response's policyDocumentUri, or a bare .pdf link) is lifted out of the text
 *    and shown as a document card that opens the document in a new tab, and
 *  - the agent's suggested follow-up questions render as one-tap chips.
 *
 * Generated policies come back as s3:// URIs the browser can't open directly, so
 * the card routes through SecurityClient.viewPolicy (WA streams the PDF bytes).
 *
 * The widget is gated by AppComponent (session valid, etc.); here we only own the
 * open/closed state and the conversation itself. In PDP the only topic is scheduling,
 * it exists only while a schedule builder is on screen (ScheduleAssistantContextService),
 * and the widget opens from the builder toolbar - the bottom-right corner belongs to the
 * PDP "Ask us a question" launcher there.
 */
@Component({
  selector: 'app-chat-bot',
  templateUrl: './chat-bot.component.html',
})
export class ChatBotComponent implements OnInit, OnDestroy {
  // Exposed for the template (global singletons, referenced directly like in AppComponent).
  Session = Session;
  environment = environment;

  showChatBot = false;
  // The widget is a set of independent chat tabs (max 3). Each ChatSession owns its
  // own running history + chatId (AskChatBot returns one answer per turn, so the UI
  // owns the transcript and threads the conversation via chatId).
  readonly maxTabs = 3;
  chatSessions: ChatSession[] = [this.createSession()];
  activeSessionIndex = 0;

  @ViewChild('chatBotPrompt') chatBotPrompt: ElementRef<HTMLInputElement>;
  @ViewChild('chatBotHistory') chatBotHistory: ElementRef<HTMLElement>;

  // Matches a markdown link [label](href) or a bare http(s)/s3/www URL.
  private static readonly LINK_RE =
    /\[([^\]]+)\]\(([^\s)]+)\)|((?:https?:\/\/|s3:\/\/|www\.)[^\s<>()\[\]]+)/gi;

  private subscriptions: Subscription[] = [];

  constructor(
    private securityClient: SecurityClient,
    private datePipe: DatePipe,
    private scheduleContext: ScheduleAssistantContextService,
    private translate: TranslateService
  ) {}

  /** Every text the user reads comes from en.json (chatBot.*). */
  private t(key: string): string {
    return this.translate.instant('chatBot.' + key);
  }

  ngOnInit() {
    this.subscriptions.push(this.scheduleContext.openRequested$.subscribe(() => this.openScheduleAssistant()));
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  /** PDP hosts only the scheduling assistant; PAP hosts config + recon. */
  get isPdp(): boolean {
    return environment.portalCode === 'PDP';
  }

  /** Whether there is anything to offer on this portal right now. */
  get available(): boolean {
    return !this.isPdp || !!this.scheduleContext.current;
  }

  get headerTitle(): string {
    return this.isPdp ? this.t('titleSchedule') : this.t('titlePap');
  }

  /** Label under assistant bubbles. */
  get assistantTag(): string {
    return this.isPdp ? 'AI' : 'PAP';
  }

  private defaultTopic(): ChatTopic {
    return this.isPdp ? 'schedule' : 'config';
  }

  private createSession(topic: ChatTopic = this.defaultTopic()): ChatSession {
    return { messages: null, chatId: null, showSpinner: false, topic: this.sanitizeTopic(topic) };
  }

  /**
   * Builder toolbar -> open on the scheduling topic. Reuses the tab the manager was last in when it
   * talks about the location on screen (or is still empty), else the latest such tab, else an empty
   * tab, else opens a new one (when under the cap). It took the FIRST matching tab, so with two
   * chats about the location a message meant for the second landed in the first (DEV 2026-10-09).
   */
  openScheduleAssistant() {
    const ctx = this.scheduleContext.current;
    if (!this.isPdp || !ctx) return;
    const matches = (s: ChatSession) => s?.topic === 'schedule' && s.customerCode === ctx.customerCode;
    const active = this.chatSessions[this.activeSessionIndex];
    let index = active && (matches(active) || !active.messages || active.messages.length === 0) ? this.activeSessionIndex : -1;
    if (index < 0) {
      for (let i = this.chatSessions.length - 1; i >= 0; i--) {
        if (matches(this.chatSessions[i])) { index = i; break; }
      }
    }
    if (index < 0) index = this.chatSessions.findIndex((s) => !s.messages || s.messages.length === 0);
    if (index < 0 && this.chatSessions.length < this.maxTabs) {
      this.chatSessions.push(this.createSession('schedule'));
      index = this.chatSessions.length - 1;
    }
    this.activeSessionIndex = index < 0 ? this.activeSessionIndex : index;
    this.showChatBot = true;
    setTimeout(() => {
      this.scrollHistoryToBottom();
      this.chatBotPrompt?.nativeElement?.focus();
    }, 1);
  }

  /**
   * Never let a chat sit on a topic this user may not use. Applied wherever a topic is set,
   * so the only way to reach the recon assistant is to actually hold the role. This is
   * convenience, not security - WebAPI and AIProxyAPI both check the role off the token.
   */
  private sanitizeTopic(topic: ChatTopic): ChatTopic {
    if (this.isPdp) return 'schedule';
    return topic === 'recon' && this.canUseReconTopic ? 'recon' : 'config';
  }

  /**
   * Whether the TabaPay reconciliation topic may be offered. Mirrors the gate on the
   * reconciliation dashboard's AI export; the server enforces it for real (WebAPI checks
   * the role before it forwards anything to the recon agent).
   */
  get canUseReconTopic(): boolean {
    return isTabapayAiRoleAllowed(Session.roleIds);
  }

  /** The topics this user may pick from. One entry = nothing to choose, so the UI hides it. */
  get availableTopics(): { value: ChatTopic; label: string }[] {
    if (this.isPdp) return [{ value: 'schedule', label: this.t('topicSchedule') }];
    const all: { value: ChatTopic; label: string }[] = [
      { value: 'config', label: this.t('topicConfig') },
      { value: 'recon', label: this.t('topicRecon') },
    ];
    return all.filter((t) => t.value !== 'recon' || this.canUseReconTopic);
  }

  topicLabel(topic: ChatTopic): string {
    const match = this.availableTopics.find((t) => t.value === topic);
    return match ? match.label : this.t('topicConfig');
  }

  /**
   * A started conversation keeps its topic: the transcript holds tool results the other
   * agent has no tools for, and the server rejects a chat continued under a different
   * topic anyway. Switching subjects means a new chat, which is what the UI offers.
   */
  get topicLocked(): boolean {
    const session = this.activeSession;
    return !!session && !!session.messages && session.messages.length > 0;
  }

  /** Switches the active (still empty) tab to another topic. */
  setTopic(topic: string) {
    const session = this.activeSession;
    if (!session || this.topicLocked) return;
    if (!this.availableTopics.some((t) => t.value === topic)) return;
    session.topic = topic as ChatTopic;
  }

  /** Greeting under the header - says what this tab's assistant can actually do. */
  get greeting(): string {
    switch (this.activeSession?.topic) {
      case 'recon':
        return this.t('greetRecon');
      case 'schedule':
        return this.t('greetSchedule');
      default:
        return this.t('greetDefault');
    }
  }

  /** The tab currently on screen (null only in the impossible empty-tabs case). */
  get activeSession(): ChatSession {
    return this.chatSessions[this.activeSessionIndex] ?? null;
  }

  openChatBot() {
    this.showChatBot = true;
  }

  closeChatBot() {
    this.showChatBot = false;
  }

  /**
   * Starts a new chat in its own tab. The current chat stays available under its
   * existing tab; the freshly created tab becomes active with an empty conversation.
   * Capped at maxTabs (3) - the button is disabled in the template once the cap is hit.
   */
  newChat() {
    if (this.chatSessions.length >= this.maxTabs) return;
    // Carry the current topic over - starting a second chat about the same subject is the
    // common case, and the selector is right there when it isn't.
    this.chatSessions.push(this.createSession(this.activeSession?.topic ?? 'config'));
    this.activeSessionIndex = this.chatSessions.length - 1;
    setTimeout(() => this.chatBotPrompt?.nativeElement?.focus(), 1);
  }

  /** Switches the visible conversation to the tab at the given index. */
  selectSession(index: number) {
    if (index < 0 || index >= this.chatSessions.length) return;
    this.activeSessionIndex = index;
    setTimeout(() => {
      this.scrollHistoryToBottom();
      this.chatBotPrompt?.nativeElement?.focus();
    }, 1);
  }

  /**
   * Closes the chat tab at the given index. Always keeps at least one tab open -
   * closing the last remaining chat starts a fresh empty one. The active tab is
   * kept pointing at the same conversation where possible.
   */
  closeSession(index: number) {
    if (index < 0 || index >= this.chatSessions.length) return;
    this.chatSessions.splice(index, 1);

    if (this.chatSessions.length === 0) {
      this.chatSessions.push(this.createSession());
      this.activeSessionIndex = 0;
      return;
    }

    if (index < this.activeSessionIndex) {
      this.activeSessionIndex--;
    } else if (index === this.activeSessionIndex) {
      this.activeSessionIndex = Math.min(
        this.activeSessionIndex,
        this.chatSessions.length - 1
      );
    }
  }

  /** Tab label: the first user message (truncated) or "New chat" when still empty. */
  tabTitle(session: ChatSession): string {
    const firstUser = session?.messages?.find((m) => m.Role === 'user');
    const text = firstUser?.Content?.trim();
    if (!text) return this.t('newChat');
    return text.length > 20 ? text.slice(0, 20) + '\u2026' : text;
  }

  /** Tooltip for a tab - the label plus which assistant that tab is talking to. */
  tabTooltip(session: ChatSession): string {
    return this.tabTitle(session) + ' \u2014 ' + this.topicLabel(session?.topic ?? 'config');
  }

  /**
   * Sends a message. With no argument it reads the prompt input; passing text
   * (used by the suggested-followup chips) sends that text directly.
   */
  sendChatBot(presetText?: string) {
    // Capture the session at send time so a response still lands in the right tab
    // even if the user switches tabs while the request is in flight.
    const session = this.activeSession;
    if (session == null) return;

    const fromInput = presetText == null;
    const message = fromInput ? this.chatBotPrompt.nativeElement.value : presetText;
    if (fromInput) {
      this.chatBotPrompt.nativeElement.value = '';
    }

    if (message != null && message != '') {
      // Fail closed. The selector never offers the recon topic without the role, so this only
      // catches a tab that outlived the role it was opened with (a re-login into another account
      // in the same browser tab) - it must not quietly ask the config assistant instead.
      if (session.topic === 'recon' && !this.canUseReconTopic) {
        session.topic = 'config';
        this.pushAssistantNote(
          session,
          this.t('reconNotAuthorized')
        );
        return;
      }

      // The scheduling assistant works on the location/week the builder shows, and a chat
      // stays on the location it started with (the server refuses a switch anyway).
      let scheduleCustomerCode: number = null;
      let scheduleWeekStart: string = null;
      if (session.topic === 'schedule') {
        const ctx = this.scheduleContext.current;
        if (!ctx) {
          this.pushAssistantNote(session, this.t('scheduleOpenLocation'));
          return;
        }
        if (session.customerCode && session.customerCode !== ctx.customerCode) {
          this.pushAssistantNote(session, this.t('scheduleOtherLocation'));
          return;
        }
        session.customerCode = ctx.customerCode;
        scheduleCustomerCode = ctx.customerCode;
        scheduleWeekStart = ctx.weekStart ?? undefined;
      }

      if (session.messages == null) session.messages = [];
      // Echo the user's message immediately - AskChatBot only returns the assistant
      // answer, so the UI owns the running history.
      const userParsed = this.parseMessage(message);
      session.messages.push({
        Role: 'user',
        Content: message,
        Sent: this.datePipe.transform(new Date(), 'HH:mm'),
        Parts: userParsed.parts,
        Pdfs: userParsed.pdfs,
      });

      var model: any = {
        Topic: session.topic,
        Question: message,
        ChatId: session.chatId,
      };
      if (session.topic === 'schedule') {
        model.CustomerCode = scheduleCustomerCode;
        model.WeekStart = scheduleWeekStart;
      }
      this.Session.suppressSpinner = true;
      // Per-tab spinner; the prompt input is disabled via [disabled] while it runs.
      session.showSpinner = true;
      setTimeout(() => this.scrollHistoryToBottom(), 1);

      this.securityClient
        .askChatBot(model)
        .then((response) => {
          this.Session.suppressSpinner = false;
          var answer = response?.result?.answer;
          // Keep the conversation id so the next turn continues the same thread.
          session.chatId = response?.result?.chatId ?? session.chatId;
          const actions: ScheduleActionCard[] = (response?.result?.scheduleActions ?? []).map((a) => this.toCard(a));
          // "Show me next week", "open the Dinner shift settings" - the screen follows the reply.
          if (session.topic === 'schedule') this.scheduleContext.runUiCommands(response?.result?.uiCommands ?? []);
          if (answer || actions.length) {
            const parsed = this.parseMessage(answer ?? '');
            // The agent may also return the generated policy as a separate field.
            this.addPdf(parsed.pdfs, response?.result?.policyDocumentUri);
            session.messages.push({
              Role: 'assistant',
              Content: answer,
              Sent: this.datePipe.transform(new Date(), 'HH:mm'),
              Parts: parsed.parts,
              Pdfs: parsed.pdfs,
              Followups: response?.result?.suggestedFollowups ?? [],
              Actions: actions,
            });
          }
          session.showSpinner = false;
          // Only touch the DOM (scroll/focus) when this tab is the one on screen.
          if (session === this.activeSession) {
            setTimeout(() => this.scrollHistoryToBottom(), 1);
            setTimeout(() => this.chatBotPrompt.nativeElement.focus(), 1);
          }
        })
        .catch(() => {
          this.Session.suppressSpinner = false;
          session.showSpinner = false;
        });
    }
  }

  /** Confirm on a scheduling card - the server runs the stored change once. */
  confirmAction(session: ChatSession, card: ScheduleActionCard) {
    this.decideAction(session, card, true);
  }

  /** Cancel on a scheduling card - nothing changes. */
  cancelAction(session: ChatSession, card: ScheduleActionCard) {
    this.decideAction(session, card, false);
  }

  private decideAction(session: ChatSession, card: ScheduleActionCard, confirm: boolean) {
    if (!session?.chatId || !session.customerCode || card.busy || card.status !== 'pending') return;
    card.busy = true;
    const model = { ChatId: session.chatId, ActionId: card.id, CustomerCode: session.customerCode };
    const call = confirm
      ? this.securityClient.confirmScheduleAction(model)
      : this.securityClient.cancelScheduleAction(model);

    this.Session.suppressSpinner = true;
    call
      .then((response) => {
        this.Session.suppressSpinner = false;
        this.applyCardState(card, response?.result);
        card.busy = false;
        if (card.status === 'executed') this.scheduleContext.notifyScheduleChanged();
      })
      .catch((error) => {
        this.Session.suppressSpinner = false;
        card.busy = false;
        // 409 = already handled or expired; the body is the card's current state.
        if (error?.status === 409 && error?.error?.status) {
          this.applyCardState(card, error.error);
          return;
        }
        card.resultMessage =
          error?.status === 403
            ? this.t('scheduleNotAuthorized')
            : this.t('unreachable');
      });
  }

  /** Pending cards of one reply - "Confirm all" shows when there are two or more. */
  pendingCards(message: ChatMessage): ScheduleActionCard[] {
    return (message.Actions ?? []).filter((c) => c.status === 'pending' && !c.busy);
  }

  /** Confirm every pending card of a reply, in the order shown, in one server call. */
  confirmAllActions(session: ChatSession, message: ChatMessage) {
    const cards = this.pendingCards(message);
    if (!session?.chatId || !session.customerCode || cards.length === 0) return;
    cards.forEach((c) => (c.busy = true));
    this.Session.suppressSpinner = true;
    this.securityClient
      .confirmAllScheduleActions({ ChatId: session.chatId, ActionIds: cards.map((c) => c.id), CustomerCode: session.customerCode })
      .then((response) => {
        const byId = new Map<string, any>((response?.result ?? []).map((d: any) => [d.id, d]));
        cards.forEach((c) => this.applyCardState(c, byId.get(c.id)));
        if (cards.some((c) => c.status === 'executed')) this.scheduleContext.notifyScheduleChanged();
      })
      .catch((error) => {
        // The server runs the cards one by one, not as one transaction: the ones before a failure
        // may have gone through. So the builder is refreshed and the text does not say "nothing changed".
        if (error?.status !== 403) this.scheduleContext.notifyScheduleChanged();
        cards.forEach((c) => {
          if (c.status === 'pending')
            c.resultMessage =
              error?.status === 403
                ? this.t('scheduleNotAuthorized')
                : this.t('unreachableBatch');
        });
      })
      .finally(() => {
        this.Session.suppressSpinner = false;
        cards.forEach((c) => (c.busy = false));
      });
  }

  private toCard(a: any): ScheduleActionCard {
    return {
      id: a?.id,
      kind: a?.kind,
      title: a?.title,
      details: a?.details ?? [],
      warnings: a?.warnings ?? [],
      status: a?.status ?? 'pending',
      resultMessage: a?.resultMessage,
    };
  }

  private applyCardState(card: ScheduleActionCard, dto: any) {
    if (!dto) return;
    card.status = dto.status ?? card.status;
    card.resultMessage = dto.resultMessage ?? card.resultMessage;
  }

  /** Status line under a decided card. */
  cardStatusLabel(card: ScheduleActionCard): string {
    switch (card.status) {
      case 'executed':
        return '\u2713 ' + (card.resultMessage || this.t('cardDone'));
      case 'failed':
        return '\u2717 ' + (card.resultMessage || this.t('cardNotDone'));
      case 'cancelled':
        return this.t('cardCancelled');
      case 'expired':
        return card.resultMessage || this.t('cardExpired');
      case 'executing':
        return this.t('cardWorking');
      default:
        return card.resultMessage || '';
    }
  }

  /** Writes a message into the transcript as if the assistant had said it (no request sent). */
  private pushAssistantNote(session: ChatSession, text: string) {
    if (session.messages == null) session.messages = [];
    const parsed = this.parseMessage(text);
    session.messages.push({
      Role: 'assistant',
      Content: text,
      Sent: this.datePipe.transform(new Date(), 'HH:mm'),
      Parts: parsed.parts,
      Pdfs: parsed.pdfs,
    });
    setTimeout(() => this.scrollHistoryToBottom(), 1);
  }

  /**
   * Opens a PDF card's document in a new tab. http(s) links open directly; an
   * s3:// policy URI is fetched through WA (it isn't browser-openable on its own).
   */
  openPdf(pdf: PdfRef) {
    const uri = pdf?.uri;
    if (!uri) return;
    if (/^https?:\/\//i.test(uri)) {
      window.open(uri, '_blank', 'noopener');
      return;
    }
    const key = this.stripS3Key(uri);
    if (key) {
      this.securityClient.viewPolicy(key);
    } else {
      window.open(uri, '_blank', 'noopener');
    }
  }

  private scrollHistoryToBottom() {
    if (this.chatBotHistory?.nativeElement) {
      this.chatBotHistory.nativeElement.scrollTop =
        this.chatBotHistory.nativeElement.scrollHeight;
    }
  }

  /** s3://bucket/<key> -> <key> (drop scheme + bucket). Null when not an s3 uri. */
  private stripS3Key(uri: string): string {
    const match = /^s3:\/\/[^/]+\/(.+)$/i.exec(uri);
    return match ? match[1] : null;
  }

  private fileName(uri: string): string {
    const path = uri.split(/[?#]/)[0];
    const name = path.substring(path.lastIndexOf('/') + 1);
    return name || 'document.pdf';
  }

  private addPdf(pdfs: PdfRef[], uri: string) {
    if (!uri) return;
    if (pdfs.some((p) => p.uri === uri)) return;
    pdfs.push({ uri, name: this.fileName(uri) });
  }

  /**
   * Splits message text into renderable parts and a list of PDF documents.
   * Markdown links and bare URLs are recognised; any link to a .pdf is removed
   * from the prose and collected as a PdfRef (shown as a card), while ordinary
   * links stay inline and clickable.
   */
  private parseMessage(text: string): { parts: ChatSegment[]; pdfs: PdfRef[] } {
    const parts: ChatSegment[] = [];
    const pdfs: PdfRef[] = [];
    if (!text) return { parts, pdfs };

    // Fresh RegExp so the shared lastIndex on the static pattern is never reused.
    const re = new RegExp(ChatBotComponent.LINK_RE.source, 'gi');
    let lastIndex = 0;
    let match: RegExpExecArray;

    while ((match = re.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: text.slice(lastIndex, match.index) });
      }

      let label: string;
      let href: string;
      let consumedEnd = match.index + match[0].length;

      if (match[1] !== undefined && match[2] !== undefined) {
        // Markdown link: [label](href)
        label = match[1];
        href = match[2];
      } else {
        // Bare URL - trailing sentence punctuation isn't part of the link.
        let raw = match[3];
        const trail = raw.match(/[.,;:!?)]+$/);
        if (trail) {
          raw = raw.slice(0, raw.length - trail[0].length);
          consumedEnd -= trail[0].length;
        }
        label = raw;
        href = raw;
      }

      const isS3 = /^s3:\/\//i.test(href);
      const normalizedHref =
        /^https?:\/\//i.test(href) || isS3 ? href : 'https://' + href;
      const pathOnly = normalizedHref.split(/[?#]/)[0].toLowerCase();

      if (pathOnly.endsWith('.pdf')) {
        // Lift PDFs out of the prose - they render as their own cards.
        this.addPdf(pdfs, normalizedHref);
      } else {
        parts.push({ text: label, href: normalizedHref });
      }

      lastIndex = consumedEnd;
      re.lastIndex = consumedEnd;
    }

    if (lastIndex < text.length) {
      parts.push({ text: text.slice(lastIndex) });
    }
    return { parts: this.tidy(parts), pdfs };
  }

  /** Drop whitespace-only text segments left behind when a PDF link is removed. */
  private tidy(parts: ChatSegment[]): ChatSegment[] {
    return parts.filter((p) => p.href || (p.text && p.text.trim().length > 0));
  }
}
